import React from 'react';
import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';
import { FANOUT_PAGES } from '../lib/site';
import { getMmseLicensingEvidenceById } from '../lib/mmseLicensingEvidence';

const page = FANOUT_PAGES.find((p) => p.path === '/formulario-e-direitos-de-uso');
const product = getMmseLicensingEvidenceById('par-mmse-product');
const permissions = getMmseLicensingEvidenceById('par-mmse-permissions');
const licensing = getMmseLicensingEvidenceById('par-licensing-team');

const ANSWER =
  'Este site não disponibiliza o formulário completo do Mini Mental (MMSE) para download. A PAR mantém a página oficial do instrumento e as regras de permissões/licenciamento; para uso do teste completo, o material deve ser obtido por canal oficial ou autorizado e usado conforme os termos vigentes. A PAR também lista Brazilian Portuguese entre as traduções autorizadas do MMSE.';

const REFERENCES = [
  `${product.authority}. ${product.title}. Página oficial do produto e lista de traduções autorizadas.`,
  `${permissions.authority}. ${permissions.title}. Política oficial para tradução, adaptação e reformatação.`,
  `${licensing.authority}. ${licensing.title}. Política oficial de reprodução e uso dos materiais.`
];

const RELATED = [
  {
    to: '/como-aplicar',
    label: 'Como aplicar o Mini Mental',
    note: 'como preservar padronização e comparabilidade sem reproduzir o roteiro.'
  },
  {
    to: '/erros-de-aplicacao',
    label: 'Erros de aplicação',
    note: 'por que improvisações e versões misturadas podem comprometer a comparação.'
  },
  {
    to: '/adaptacoes-sensoriais',
    label: 'Adaptações sensoriais',
    note: 'quando corrigir uma barreira e quando uma modificação muda a interpretação.'
  },
  {
    to: '/interpretacao-dos-resultados',
    label: 'Como interpretar o resultado',
    note: 'licença de uso e validade da interpretação clínica são questões diferentes.'
  },
  {
    to: '/pontos-de-corte',
    label: 'Pontos de corte e referências brasileiras',
    note: 'use referências adequadas à população, idioma e contexto.'
  }
];

export default function FormularioEDireitosDeUso() {
  return (
    <ArticleLayout page={page} answer={ANSWER} related={RELATED} references={REFERENCES}>
      <section aria-labelledby="pdf-oficial-gratuito">
        <h2 id="pdf-oficial-gratuito" className="text-2xl mb-5">Existe PDF oficial gratuito do Mini Mental?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          A página oficial da PAR não oferece o formulário completo do MMSE como um download público
          livre. Ela apresenta o instrumento como produto publicado e direciona o uso, aquisição e
          pedidos de permissão pelos canais próprios da editora.
        </p>
        <p className="text-[var(--text-muted)]">
          Isso é diferente de afirmar que todo arquivo gratuito encontrado na internet é necessariamente
          ilegal. Sem conhecer a origem e a autorização de um arquivo específico, essa conclusão não pode
          ser feita de forma responsável.
        </p>
      </section>

      <section aria-labelledby="o-que-par-controla">
        <h2 id="o-que-par-controla" className="text-2xl mb-5">O que as regras da PAR dizem sobre reprodução e modificação?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          A política oficial da PAR informa que seus materiais não devem ser fotocopiados livremente e
          que reprodução ou modificação dependem das condições de permissão aplicáveis. A própria PAR
          orienta solicitar autorização para reformatar, adaptar ou traduzir seus instrumentos.
        </p>
        <p className="text-[var(--text-muted)]">
          Para o MMSE especificamente, a página oficial também direciona pedidos de uso de versões
          modificadas — inclusive formato modificado ou tradução — ao processo formal de permissão.
        </p>
      </section>

      <section aria-labelledby="portugues-brasileiro">
        <h2 id="portugues-brasileiro" className="text-2xl mb-5">Existe versão autorizada em português brasileiro?</h2>
        <p className="text-[var(--text-muted)]">
          <strong>Sim.</strong> A PAR lista <em>Brazilian Portuguese</em> entre as traduções autorizadas
          atualmente disponíveis para o MMSE. Isso é relevante porque usar uma tradução identificada e
          autorizada é diferente de improvisar a tradução de comandos ou itens durante a aplicação.
        </p>
      </section>

      <section aria-labelledby="como-obter">
        <h2 id="como-obter" className="text-2xl mb-5">Como obter uma versão legítima para uso?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          O ponto de partida é a página oficial do MMSE na PAR ou um canal autorizado indicado para sua
          região e finalidade. As condições de aquisição, qualificação profissional, reprodução e uso
          podem mudar; por isso, devem ser conferidas nos termos vigentes no momento da obtenção.
        </p>
        <p className="text-[var(--text-muted)]">
          Se a necessidade envolve tradução, adaptação, reformatação, pesquisa, reprodução institucional
          ou incorporação do instrumento a outro material, a PAR mantém um processo próprio de pedidos de
          permissão e licenciamento.
        </p>
      </section>

      <section aria-labelledby="licenca-validade">
        <h2 id="licenca-validade" className="text-2xl mb-5">Licença de uso e validade clínica são a mesma coisa?</h2>
        <p className="text-[var(--text-muted)] mb-4"><strong>Não.</strong> São questões diferentes.</p>
        <p className="text-[var(--text-muted)]">
          Obter um material por canal autorizado resolve a questão de origem e uso conforme os termos
          aplicáveis, mas não garante que qualquer ponto de corte, norma ou interpretação seja adequada
          a qualquer paciente. A interpretação continua dependendo do idioma, população, escolaridade,
          contexto de aplicação e da evidência que sustenta a referência escolhida — veja{' '}
          <Link to="/interpretacao-dos-resultados" className=" text-[var(--primary)] hover:underline">
            como interpretar o resultado
          </Link>{' '}e{' '}
          <Link to="/pontos-de-corte" className=" text-[var(--primary)] hover:underline">
            pontos de corte e referências brasileiras
          </Link>.
        </p>
      </section>

      <section aria-labelledby="versao-online">
        <h2 id="versao-online" className="text-2xl mb-5">Posso usar uma versão encontrada online?</h2>
        <p className="text-[var(--text-muted)] mb-4">
          Antes de usar qualquer arquivo em contexto clínico, é necessário <strong>verificar a origem</strong>,
          a versão do instrumento, o idioma, a autorização de uso e se o conteúdo foi modificado. Um arquivo
          sem procedência clara não deve ser presumido equivalente ao material publicado pela PAR.
        </p>
        <p className="text-[var(--text-muted)]">
          Mesmo quando a origem é legítima, misturar versões, reformular instruções ou alterar tarefas
          pode reduzir a comparabilidade com as referências usadas para interpretar o resultado. Veja{' '}
          <Link to="/erros-de-aplicacao" className=" text-[var(--primary)] hover:underline">
            erros de aplicação
          </Link>{' '}e{' '}
          <Link to="/adaptacoes-sensoriais" className=" text-[var(--primary)] hover:underline">
            adaptações sensoriais
          </Link>.
        </p>
      </section>

      <section aria-labelledby="site-fornece">
        <h2 id="site-fornece" className="text-2xl mb-5">O MiniMental.com.br fornece o formulário?</h2>
        <p className="text-[var(--text-muted)] mb-4"><strong>Não.</strong></p>
        <p className="text-[var(--text-muted)]">
          O site não reproduz perguntas, comandos, formulário ou critérios literais do MMSE. A
          calculadora recebe pontos já obtidos e o conteúdo editorial ajuda a entender aplicação,
          referências e limites de interpretação. Para o processo geral, consulte{' '}
          <Link to="/como-aplicar" className=" text-[var(--primary)] hover:underline">
            como aplicar o Mini Mental
          </Link>.
        </p>
      </section>

      <section aria-labelledby="fluxo-final">
        <h2 id="fluxo-final" className="text-2xl mb-5">Fluxo seguro</h2>
        <p className="text-[var(--text-muted)] font-medium">
          identificar versão → obter por canal autorizado → preservar formato e instruções → documentar idioma e versão → interpretar com referência adequada
        </p>
      </section>
    </ArticleLayout>
  );
}
